import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Card, Typography, Alert, Space, Divider } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { loginRequest } from '@/store/auth/authSlice';
import { selectIsAuthenticated, selectAuthLoading, selectAuthError } from '@/store/auth/authSelectors';
import logger from '@/utils/logger';

const { Title } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);

  useEffect(() => {
    logger.log('LoginPage - isAuthenticated changed:', isAuthenticated);
    if (isAuthenticated) {
      logger.log('LoginPage - Authenticated, navigating to /companies');
      // Use a small delay to ensure Redux state is fully propagated
      // This prevents race conditions with ProtectedRoute
      const timer = setTimeout(() => {
        navigate('/companies', { replace: true });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (values: LoginFormValues) => {
    dispatch(loginRequest(values));
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f0f2f5',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 420, margin: '0 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ margin: 0 }}>
              Zonka Admin
            </Title>
          </div>

          {error && <Alert description={error} type="error" showIcon closable />}

          <Form<LoginFormValues>
            name="login"
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Password" />
            </Form.Item>

            <Form.Item name="remember" valuePropName="checked">
              <Checkbox>Remember me</Checkbox>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Sign In
              </Button>
            </Form.Item>
          </Form>

          <Divider plain>or sign in with</Divider>

          <Space style={{ width: '100%', justifyContent: 'center' }}>
            {/* Google and LinkedIn OAuth buttons will be added here */}
            <Button disabled>Google</Button>
            <Button disabled>LinkedIn</Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
}
